import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faRocket, faExclamationTriangle, faCheckCircle,
  faSpinner, faServer, faClock, faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { Breadcrumb } from '../components/Breadcrumb';
import { CommandConfirmationModal } from '../components/CommandConfirmationModal';
import { ExecutionTracker } from '../components/ExecutionTracker';
import { SolutionValidationBadge } from '../components/SolutionValidationBadge';
import { navigationState } from '../utils/navigationState';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

interface ClusterUpgradePageProps {
  clusterId: string;
  clusterName: string;
  authToken: string | null;
  onBack: () => void;
}

export const ClusterUpgradePage: React.FC<ClusterUpgradePageProps> = ({
  clusterId,
  clusterName,
  authToken,
  onBack
}) => {
  const [loading, setLoading] = useState(true);
  const [upgradeAnalysis, setUpgradeAnalysis] = useState<any>(null);
  const [showWizard, setShowWizard] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [upgradeStarted, setUpgradeStarted] = useState(false);
  const [upgradeMode, setUpgradeMode] = useState<'zero-downtime' | 'force'>('zero-downtime');
  const [isNodeSync, setIsNodeSync] = useState(false);
  const [executingFix, setExecutingFix] = useState<'option1' | 'option2' | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'logs'>('overview');

  // Confirmation modal state
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingSolution, setPendingSolution] = useState<any>(null);
  const [isExecutingCommand, setIsExecutingCommand] = useState(false);

  // Execution tracking state
  const [currentExecutionId, setCurrentExecutionId] = useState<string | null>(null);
  const [showExecutionTracker, setShowExecutionTracker] = useState(false);

  // Progress tracking
  const [upgradeProgress, setUpgradeProgress] = useState({
    phase: 'idle',
    progress: 0,
    elapsed: 0,
    remaining: 0,
    logs: [] as Array<{ time: string; message: string; type: string }>
  });

  useEffect(() => {
    fetchUpgradeAnalysis();

    // Check if we're in node-sync mode
    const currentView = navigationState.getCurrentView();
    if (currentView.data === 'node-sync') {
      setIsNodeSync(true);
    }

    // Check if there's an active upgrade
    const activeUpgrade = navigationState.getUpgradeState();
    if (activeUpgrade && activeUpgrade.status === 'in_progress') {
      setShowWizard(false);
      setUpgradeStarted(true);
      startProgressMonitoring();
    }
  }, []);

  const fetchUpgradeAnalysis = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/accounts/eks/clusters/${clusterId}/upgrade-analysis`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      const data = await response.json();
      setUpgradeAnalysis(data);
    } catch (error) {
      console.error('Error fetching upgrade analysis:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartUpgrade = async (targetVersion: string, mode: 'zero-downtime' | 'force') => {
    console.log('🚀 handleStartUpgrade CALLED', { targetVersion, mode, isNodeSync, clusterId });

    // ALWAYS show logs immediately
    setUpgradeStarted(true);

    // Clear any existing error fix options
    sessionStorage.removeItem('llmErrorAnalysis');
    sessionStorage.removeItem('amiMigrationError');

    // Check if there's an incremental upgrade plan
    const incrementalPlanStr = sessionStorage.getItem('incrementalUpgradePlan');
    let actualTargetVersion = targetVersion;

    if (incrementalPlanStr && isNodeSync) {
      const plan = JSON.parse(incrementalPlanStr);
      console.log('📋 Incremental upgrade plan found:', plan);

      if (plan.step === 1) {
        // First step: upgrade to intermediate version
        actualTargetVersion = plan.intermediateVersion;
        addLog('', 'info');
        addLog('🔧 EXECUTING INCREMENTAL UPGRADE (Step 1 of 2)', 'info');
        addLog(`Upgrading to intermediate version ${actualTargetVersion}`, 'info');
        addLog('This may trigger AWS automatic AL2→AL2023 migration', 'info');
        addLog('', 'info');
      }
    }

    setUpgrading(true);
    if (isNodeSync) {
      addLog(`🚀 Starting node group synchronization (${mode === 'force' ? 'FORCE' : 'ZERO-DOWNTIME'})...`, 'info');
    } else {
      addLog(`🚀 Starting ${mode === 'force' ? 'FORCE' : 'ZERO-DOWNTIME'} upgrade...`, 'info');
    }

    try {
      const isForceMode = mode === 'force';

      // For node sync, use the sync-node-groups endpoint
      const endpoint = isNodeSync
        ? `${API_BASE_URL}/api/accounts/eks/clusters/${clusterId}/sync-node-groups`
        : `${API_BASE_URL}/api/accounts/eks/clusters/${clusterId}/upgrade`;

      console.log('🌐 Making fetch request to:', endpoint);
      console.log('📦 Request body:', {
        targetVersion: actualTargetVersion,
        upgradeNodeGroups: true,
        upgradeAddons: !isNodeSync,
        drainNodes: !isForceMode,
        force: isForceMode
      });

      setUpgrading(true);

      const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            targetVersion: actualTargetVersion, // Use the actual target (may be intermediate)
            upgradeNodeGroups: true,
            upgradeAddons: !isNodeSync, // Don't upgrade addons when syncing nodes
            drainNodes: !isForceMode, // Drain nodes for zero-downtime
            force: isForceMode
          })
        }
      );

      console.log('✅ Fetch response received:', response.status, response.statusText);

      if (response.ok) {
        const result = await response.json();
        console.log('📊 Response data:', JSON.stringify(result, null, 2));

        // Check if sync/upgrade actually succeeded
        // For node sync, check if any upgrades failed
        if (isNodeSync && result.upgrades) {
          const failedUpgrades = result.upgrades.filter((u: any) => u.status === 'failed');
          const successfulUpgrades = result.upgrades.filter((u: any) => u.status === 'initiated');

          if (failedUpgrades.length > 0 && successfulUpgrades.length === 0) {
            // All upgrades failed - show detailed error
            addLog('', 'error');
            addLog('═══════════════════════════════════════════', 'error');
            addLog('❌ NODE GROUP UPGRADE FAILED', 'error');
            addLog('═══════════════════════════════════════════', 'error');
            addLog('', 'error');
            addLog('💡 TIP: Check the "Logs" tab above for detailed error information', 'info');
            addLog('', 'error');

            failedUpgrades.forEach((upgrade: any) => {
              addLog(`📦 Node Group: ${upgrade.nodeGroupName}`, 'error');
              addLog(`🎯 Attempted: ${upgrade.fromVersion} → ${upgrade.toVersion}`, 'error');
              addLog(`❌ Error: ${upgrade.error}`, 'error');
              addLog('', 'error');
            });

            // 🤖 DISPLAY LLM+MCP INTELLIGENT ANALYSIS
            if (result.llmAnalysis && result.llmAnalysis.success) {
              addLog('🤖 AI ANALYSIS (LLM + EKS MCP Server):', 'info');
              addLog('', 'info');
              addLog(`🔍 ROOT CAUSE: ${result.llmAnalysis.rootCause}`, 'warning');
              addLog(`⚠️ CRITICALITY: ${result.llmAnalysis.criticality.toUpperCase()}`, 'warning');
              addLog(`📊 IMPACT: ${result.llmAnalysis.impact}`, 'warning');
              addLog('', 'info');

              // Display solutions
              if (result.llmAnalysis.solutions && result.llmAnalysis.solutions.length > 0) {
                addLog('💡 RECOMMENDED SOLUTIONS:', 'success');
                addLog('', 'success');

                result.llmAnalysis.solutions.forEach((sol: any, idx: number) => {
                  addLog(`Option ${idx + 1}: ${sol.title}`, 'success');
                  addLog(`   Description: ${sol.description}`, 'info');
                  addLog(`   Downtime: ${sol.estimatedDowntime}`, 'info');
                  addLog(`   Automated: ${sol.automated ? 'Yes' : 'No'}`, 'info');
                  addLog('', 'info');

                  // Store solution details for UI buttons
                  sessionStorage.setItem(`llmSolution${idx + 1}`, JSON.stringify({
                    ...sol,
                    clusterName: clusterName,
                    clusterId: clusterId,
                    failedUpgrade: failedUpgrades[0]
                  }));
                });

                // Set state to show LLM fix options
                sessionStorage.setItem('llmErrorAnalysis', JSON.stringify({
                  analysis: result.llmAnalysis,
                  showFixOptions: true
                }));
              }

              // Prevention tips
              if (result.llmAnalysis.preventionTips && result.llmAnalysis.preventionTips.length > 0) {
                addLog('🛡️ PREVENTION TIPS:', 'info');
                result.llmAnalysis.preventionTips.forEach((tip: string) => {
                  addLog(`   • ${tip}`, 'info');
                });
                addLog('', 'info');
              }
            } else {
              // Fallback to simple AMI error detection (backwards compatible)
              // Check if error is AMI type related
              const amiError = failedUpgrades.find((u: any) => u.error && u.error.includes('AMI Type'));
              if (amiError) {
                addLog('🔍 ROOT CAUSE IDENTIFIED:', 'warning');
                addLog('AWS EKS requires AL2023 AMI for Kubernetes 1.33+', 'warning');
                addLog('Your node group is currently using AL2 (Amazon Linux 2)', 'warning');
                addLog('', 'warning');

                // Parse versions to check if incremental upgrade is possible
                const fromVersion = amiError.fromVersion;
                const toVersion = amiError.toVersion;
                const fromParts = fromVersion.split('.');
                const toParts = toVersion.split('.');
                const fromMinor = parseInt(fromParts[1]);
                const toMinor = parseInt(toParts[1]);

                // Always show this is an AL2 → AL2023 migration issue
                addLog('💡 SOLUTION: AWS requires AL2023 AMI for Kubernetes 1.33+', 'info');
                addLog('We need to create a new node group with AL2023 AMI', 'info');
                addLog('', 'info');
                addLog('🔧 AUTOMATIC FIX OPTIONS:', 'success');
                addLog('Option 1: Create new AL2023 node group automatically', 'success');
                addLog('Option 2: Manual migration (you do it yourself)', 'success');
                addLog('', 'info');

                // Store error details for fix options
                sessionStorage.setItem('amiMigrationError', JSON.stringify({
                  nodeGroupName: amiError.nodeGroupName,
                  currentVersion: fromVersion,
                  targetVersion: toVersion,
                  clusterName: clusterName,
                  clusterId: clusterId,
                  showFixOptions: true
                }));

                // Keep wizard visible to show fix options
                // Note: Don't set upgradeStarted=false, keep logs visible
              } else {
                addLog('💡 Please check AWS Console for more details', 'info');
              }
            }

            addLog('', 'error');
            addLog('═══════════════════════════════════════════', 'error');
            return; // Don't proceed to progress view
          } else if (failedUpgrades.length > 0 && successfulUpgrades.length > 0) {
            // Partial success
            addLog('⚠️ PARTIAL SUCCESS', 'warning');
            addLog('', 'warning');
            successfulUpgrades.forEach((upgrade: any) => {
              addLog(`✅ ${upgrade.nodeGroupName}: ${upgrade.fromVersion} → ${upgrade.toVersion}`, 'success');
            });
            failedUpgrades.forEach((upgrade: any) => {
              addLog(`❌ ${upgrade.nodeGroupName}: ${upgrade.error}`, 'error');
            });
            addLog('', 'warning');
          }
        }

        // Save upgrade state
        const estimatedTime = mode === 'force'
          ? upgradeAnalysis.estimatedTime?.forceUpgrade?.total
          : upgradeAnalysis.estimatedTime?.zeroDowntime?.total;

        navigationState.goToUpgradeProgress(
          clusterId,
          clusterName,
          targetVersion,
          estimatedTime || 50
        );

        // Only hide wizard if there are no errors to fix
        // Check if there are active error fix options
        const hasErrorFixOptions = sessionStorage.getItem('llmErrorAnalysis') || sessionStorage.getItem('amiMigrationError');
        if (!hasErrorFixOptions) {
          setShowWizard(false);
        }
        setUpgradeStarted(true);
        if (isNodeSync) {
          addLog('✅ Node group sync initiated successfully', 'success');
          addLog(`📊 Mode: ${mode === 'force' ? 'Force (with downtime)' : 'Zero-downtime (rolling update)'}`, 'info');
          addLog(`🎯 Syncing node groups to version ${targetVersion}`, 'info');
        } else {
          addLog('✅ Upgrade initiated successfully', 'success');
          addLog(`📊 Mode: ${mode === 'force' ? 'Force (with downtime)' : 'Zero-downtime (rolling update)'}`, 'info');
          addLog(`🎯 Upgrading from ${upgradeAnalysis.cluster.currentVersion} to ${targetVersion}`, 'info');
        }

        if (result.upgradePlan?.steps) {
          result.upgradePlan.steps.forEach((step: any, idx: number) => {
            addLog(`📋 Step ${idx + 1}: ${step.message}`, 'info');
          });
        }

        startProgressMonitoring();
      } else {
        const errorData = await response.json();

        // Display error with detailed information
        addLog('', 'error');
        addLog('═══════════════════════════════════════════', 'error');
        addLog('❌ UPGRADE FAILED', 'error');
        addLog('═══════════════════════════════════════════', 'error');
        addLog('', 'error');
        addLog(`Error: ${errorData.message || 'Unknown error'}`, 'error');

        if (errorData.error) {
          addLog(`Details: ${errorData.error}`, 'error');
          addLog('', 'error');

          // Check for IAM permission errors
          if (errorData.error.includes('not authorized to perform') ||
              errorData.error.includes('AccessDenied') ||
              errorData.error.includes('UnauthorizedOperation')) {
            addLog('🔍 IAM PERMISSION ERROR DETECTED', 'warning');
            addLog('', 'warning');
            addLog('This error indicates missing IAM permissions.', 'warning');
            addLog('Common causes:', 'info');
            addLog('• Missing IAM role permissions', 'info');
            addLog('• Incorrect trust policy on IAM role', 'info');
            addLog('• Missing service-linked role', 'info');
            addLog('', 'info');

            // Trigger LLM analysis for IAM errors
            addLog('🤖 Triggering AI analysis for this error...', 'info');
            addLog('', 'info');

            // Make API call to analyze the error with LLM
            try {
              const analysisResponse = await fetch(
                `${API_BASE_URL}/api/accounts/eks/clusters/${clusterId}/analyze-error`,
                {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({
                    operation: isNodeSync ? 'node_group_sync' : 'cluster_upgrade',
                    errorMessage: errorData.error,
                    errorContext: {
                      statusCode: response.status,
                      endpoint: endpoint,
                      targetVersion: actualTargetVersion
                    }
                  })
                }
              );

              if (analysisResponse.ok) {
                const llmResult = await analysisResponse.json();
                if (llmResult.analysis && llmResult.analysis.success) {
                  const analysis = llmResult.analysis;

                  addLog('🤖 AI ANALYSIS COMPLETE', 'info');
                  addLog('', 'info');
                  addLog(`🔍 ROOT CAUSE: ${analysis.rootCause}`, 'warning');
                  addLog(`⚠️ CRITICALITY: ${analysis.criticality.toUpperCase()}`, 'warning');
                  addLog(`📊 IMPACT: ${analysis.impact}`, 'warning');
                  addLog('', 'info');

                  if (analysis.solutions && analysis.solutions.length > 0) {
                    addLog('💡 RECOMMENDED SOLUTIONS:', 'success');
                    addLog('', 'success');

                    analysis.solutions.forEach((sol: any, idx: number) => {
                      addLog(`Option ${idx + 1}: ${sol.title}`, 'success');
                      addLog(`   Description: ${sol.description}`, 'info');
                      addLog(`   Downtime: ${sol.estimatedDowntime}`, 'info');
                      addLog(`   Automated: ${sol.automated ? 'Yes' : 'No'}`, 'info');
                      addLog('', 'info');

                      // Store solution details for UI buttons
                      sessionStorage.setItem(`llmSolution${idx + 1}`, JSON.stringify({
                        ...sol,
                        clusterName: clusterName,
                        clusterId: clusterId
                      }));
                    });

                    // Set state to show LLM fix options
                    sessionStorage.setItem('llmErrorAnalysis', JSON.stringify({
                      analysis: analysis,
                      showFixOptions: true
                    }));

                    // Keep wizard visible to show fix options
                    setShowWizard(true);
                  }

                  if (analysis.preventionTips && analysis.preventionTips.length > 0) {
                    addLog('🛡️ PREVENTION TIPS:', 'info');
                    analysis.preventionTips.forEach((tip: string) => {
                      addLog(`   • ${tip}`, 'info');
                    });
                    addLog('', 'info');
                  }
                }
              }
            } catch (analysisError) {
              console.error('Failed to analyze error with LLM:', analysisError);
              addLog('⚠️ Could not analyze error with AI', 'warning');
            }
          }
        }

        addLog('', 'error');
        addLog('═══════════════════════════════════════════', 'error');
      }
    } catch (error: any) {
      console.error('Error starting upgrade:', error);

      // Display network/system errors
      addLog('', 'error');
      addLog('═══════════════════════════════════════════', 'error');
      addLog('❌ SYSTEM ERROR', 'error');
      addLog('═══════════════════════════════════════════', 'error');
      addLog('', 'error');
      addLog(`Error: ${error.message || 'Network error'}`, 'error');
      addLog('', 'error');

      // Try to analyze network errors with LLM
      addLog('🤖 Triggering AI analysis for this error...', 'info');
      try {
        const analysisResponse = await fetch(
          `${API_BASE_URL}/api/accounts/eks/clusters/${clusterId}/analyze-error`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${authToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              operation: isNodeSync ? 'node_group_sync' : 'cluster_upgrade',
              errorMessage: error.message || 'Network connectivity error',
              errorContext: {
                type: 'network_error',
                stack: error.stack
              }
            })
          }
        );

        if (analysisResponse.ok) {
          const llmResult = await analysisResponse.json();
          if (llmResult.analysis && llmResult.analysis.success) {
            const analysis = llmResult.analysis;

            addLog('🤖 AI ANALYSIS COMPLETE', 'info');
            addLog('', 'info');
            addLog(`🔍 ROOT CAUSE: ${analysis.rootCause}`, 'warning');
            addLog(`⚠️ CRITICALITY: ${analysis.criticality.toUpperCase()}`, 'warning');
            addLog('', 'info');

            if (analysis.solutions && analysis.solutions.length > 0) {
              addLog('💡 RECOMMENDED SOLUTIONS:', 'success');
              analysis.solutions.forEach((sol: any, idx: number) => {
                addLog(`${idx + 1}. ${sol.title}`, 'success');
                addLog(`   ${sol.description}`, 'info');
                addLog('', 'info');
              });
            }
          }
        }
      } catch (analysisError) {
        console.error('Failed to analyze error with LLM:', analysisError);
      }

      addLog('═══════════════════════════════════════════', 'error');
    } finally {
      setUpgrading(false);
    }
  };

  const startProgressMonitoring = () => {
    const startTime = Date.now();
    let lastPhase = '';
    let lastUpdateId = '';

    const pollStatus = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/accounts/eks/clusters/${clusterId}/upgrade-status`,
          {
            headers: {
              'Authorization': `Bearer ${authToken}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          const elapsed = Math.floor((Date.now() - startTime) / 1000);

          // Debug: Log the actual response
          console.log('📊 Upgrade status response:', data);

          // Log phase changes
          if (data.currentPhase !== lastPhase) {
            lastPhase = data.currentPhase;

            console.log(`🔄 Phase changed to: ${data.currentPhase}`);

            if (data.currentPhase === 'control_plane_upgrading') {
              addLog('🔄 Control plane upgrade in progress...', 'info');
              if (data.activeUpgrade) {
                addLog(`   Update ID: ${data.activeUpgrade.id}`, 'info');
                addLog(`   Status: ${data.activeUpgrade.status}`, 'info');
              }
            } else if (data.currentPhase === 'node_groups_upgrading') {
              addLog('🔄 Node groups being upgraded...', 'info');
              if (data.detailedStatus?.nodeGroups) {
                data.detailedStatus.nodeGroups.forEach((ng: any) => {
                  addLog(`   ${ng.name}: ${ng.status}`, 'info');
                });
              }
            } else if (data.currentPhase === 'pending_node_group_upgrade') {
              addLog('⏳ Control plane upgraded. Node groups pending...', 'warning');
              addLog('   You may need to manually upgrade node groups', 'warning');
              if (data.detailedStatus?.nodeGroups) {
                data.detailedStatus.nodeGroups.forEach((ng: any) => {
                  addLog(`   ${ng.name}: ${ng.currentVersion} → needs upgrade to ${ng.targetVersion || data.cluster?.currentVersion}`, 'info');
                });
              }
            } else if (data.currentPhase === 'completed') {
              addLog('✅ Upgrade completed successfully!', 'success');
              addLog(`🎉 Cluster now running version ${data.cluster.currentVersion}`, 'success');
              navigationState.updateUpgradeState('completed');
            } else if (data.currentPhase === 'failed') {
              addLog('❌ Upgrade failed', 'error');
              if (data.errorAnalysis) {
                addLog(`   Error: ${data.detailedStatus?.error?.errorMessage || 'Unknown error'}`, 'error');
                if (data.errorAnalysis.suggestions) {
                  data.errorAnalysis.suggestions.forEach((suggestion: string) => {
                    addLog(`   💡 ${suggestion}`, 'warning');
                  });
                }
              }
              navigationState.updateUpgradeState('failed');
            } else if (data.currentPhase === 'idle') {
              // No active upgrade - might mean it completed or node groups need sync
              if (data.cluster && data.nodeGroups) {
                const outOfSync = data.nodeGroups.some((ng: any) =>
                  ng.version !== data.cluster.currentVersion
                );
                if (outOfSync) {
                  addLog('⚠️ Control plane upgrade complete, but node groups need synchronization', 'warning');
                  addLog('   Use the "Sync Node Groups" option from cluster details', 'info');
                }
              }
            }
          }

          // Log recent updates
          if (data.updates && data.updates.length > 0) {
            const latestUpdate = data.updates[0];
            if (latestUpdate.id !== lastUpdateId) {
              lastUpdateId = latestUpdate.id;
              console.log(`📝 New update detected: ${latestUpdate.id}, status: ${latestUpdate.status}`);
              if (latestUpdate.errors && latestUpdate.errors.length > 0) {
                latestUpdate.errors.forEach((error: any) => {
                  addLog(`❌ ${error.errorMessage}`, 'error');
                });
              }
            }
          }

          // Calculate progress based on phase
          let progress = 0;
          let shouldStopPolling = false;

          if (data.currentPhase === 'control_plane_upgrading') {
            progress = 30;
          } else if (data.currentPhase === 'pending_node_group_upgrade') {
            progress = 50;
            // If we're in node sync mode or this is a control plane only upgrade
            // Consider this complete - user needs to manually sync nodes or they're already in sync
            const isControlPlaneOnly = !data.nodeGroups || data.nodeGroups.every((ng: any) =>
              ng.status === 'ACTIVE' && ng.version === data.cluster.currentVersion
            );

            if (isControlPlaneOnly || isNodeSync) {
              progress = 100;
              shouldStopPolling = true;
              addLog('', 'success');
              addLog('✅ Control plane upgrade complete!', 'success');
              addLog(`🎉 Cluster control plane now running version ${data.cluster.currentVersion}`, 'success');

              if (data.nodeGroups && data.nodeGroups.some((ng: any) => ng.version !== data.cluster.currentVersion)) {
                addLog('', 'info');
                addLog('📋 Next Steps:', 'warning');
                addLog('1. Node groups need to be upgraded separately', 'warning');
                addLog('2. Use the "Sync Node Groups" button from cluster details', 'warning');
              }
              navigationState.updateUpgradeState('completed');
            }
          } else if (data.currentPhase === 'node_groups_upgrading') {
            progress = 75;
          } else if (data.currentPhase === 'completed') {
            progress = 100;
            shouldStopPolling = true;
          } else if (data.currentPhase === 'idle') {
            // If idle but control plane upgraded, mark as complete
            if (data.cluster && data.cluster.currentVersion) {
              const targetVersion = navigationState.getUpgradeState()?.targetVersion;
              if (targetVersion && data.cluster.currentVersion === targetVersion) {
                progress = 100;
                shouldStopPolling = true;
                addLog('', 'success');
                addLog('✅ Upgrade complete!', 'success');
                addLog(`🎉 Cluster now running version ${data.cluster.currentVersion}`, 'success');
                navigationState.updateUpgradeState('completed');
              }
            }
          } else if (data.currentPhase === 'failed') {
            progress = 0;
            shouldStopPolling = true;
          }

          console.log(`📈 Setting progress to ${progress}%`);

          setUpgradeProgress(prev => ({
            ...prev,
            phase: data.detailedStatus?.phase || data.currentPhase,
            progress,
            elapsed
          }));

          // Stop polling if complete or failed
          if (shouldStopPolling) {
            clearInterval(interval);
          }
        }
      } catch (error) {
        console.error('Error polling upgrade status:', error);
        addLog('⚠️ Failed to fetch upgrade status', 'warning');
      }
    };

    // Poll every 10 seconds
    const interval = setInterval(pollStatus, 10000);
    pollStatus(); // Initial poll

    return () => clearInterval(interval);
  };

  const addLog = (message: string, type: string) => {
    setUpgradeProgress(prev => ({
      ...prev,
      logs: [...prev.logs, {
        time: new Date().toLocaleTimeString(),
        message,
        type
      }]
    }));
  };

  // Execute solution with confirmation
  const handleExecuteSolution = async (solution: any) => {
    setIsExecutingCommand(true);
    setUpgradeStarted(true);
    setActiveTab('logs');

    addLog('', 'info');
    addLog(`🤖 EXECUTING: ${solution.title}`, 'info');
    addLog(`📝 ${solution.description}`, 'info');
    addLog(`⏱️ Estimated Downtime: ${solution.estimatedDowntime}`, 'warning');
    addLog('', 'info');

    if (solution.automated && solution.apiEndpoint) {
      // Automated solution - call the API endpoint
      try {
        addLog(`🔧 Executing automated fix via API...`, 'info');
        const response = await fetch(
          `${API_BASE_URL}${solution.apiEndpoint}`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${authToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              clusterName: clusterName,
              clusterId: clusterId,
              solution: solution
            })
          }
        );

        if (response.ok) {
          const result = await response.json();
          addLog('✅ Automated fix completed successfully!', 'success');
          addLog(`📊 Result: ${result.message || 'Operation completed'}`, 'success');
          if (solution.steps) {
            addLog('', 'info');
            addLog('📋 Steps executed:', 'info');
            solution.steps.forEach((step: string, stepIdx: number) => {
              addLog(`  ${stepIdx + 1}. ${step}`, 'info');
            });
          }
          sessionStorage.removeItem('llmErrorAnalysis');
        } else {
          const errorData = await response.json();
          addLog('❌ Automated fix failed', 'error');
          addLog(`Error: ${errorData.message || 'Unknown error'}`, 'error');
        }
      } catch (err: any) {
        addLog('❌ Network error during automated fix', 'error');
        addLog(`${err.message}`, 'error');
      }
    } else {
      // Manual solution - execute AWS CLI commands through backend
      try {
        addLog(`🔧 Executing AWS CLI commands through backend...`, 'info');
        addLog('', 'info');

        // Log which commands will be executed
        if (solution.awsCommands && solution.awsCommands.length > 0) {
          addLog('💻 Commands to execute:', 'info');
          solution.awsCommands.forEach((cmd: string, idx: number) => {
            addLog(`  ${idx + 1}. ${cmd}`, 'info');
          });
          addLog('', 'info');
        }

        // Call backend to execute commands
        const response = await fetch(
          `${API_BASE_URL}/api/accounts/eks/clusters/${clusterId}/execute-solution`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${authToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              clusterName: clusterName,
              commands: solution.awsCommands || [],
              steps: solution.steps || []
            })
          }
        );

        if (response.ok) {
          const result = await response.json();

          // 🆕 Track execution ID if provided
          if (result.executionId) {
            setCurrentExecutionId(result.executionId);
            setShowExecutionTracker(true);
            addLog(`📊 Execution ID: ${result.executionId}`, 'info');
            addLog('', 'info');
          }

          // 🆕 Check if execution can be resumed
          if (result.canResume && result.resumeInstructions) {
            addLog('⚠️ Execution paused due to permission error', 'warning');
            addLog(result.resumeInstructions.message, 'warning');
            addLog('', 'warning');
            if (result.resumeInstructions.requiredPermissions) {
              addLog('Required permissions:', 'warning');
              result.resumeInstructions.requiredPermissions.forEach((perm: string) => {
                addLog(`   • ${perm}`, 'warning');
              });
              addLog('', 'info');
            }
            addLog('👉 Fix the permissions and click the "Resume" button below', 'info');
            addLog('', 'info');
          }

          addLog('✅ Commands executed successfully!', 'success');
          addLog('', 'info');

          // Display results for each command
          if (result.results && result.results.length > 0) {
            result.results.forEach((cmdResult: any, idx: number) => {
              addLog(`━━━ Command ${idx + 1} ━━━`, 'info');
              addLog(`$ ${cmdResult.command}`, 'info');

              if (cmdResult.status === 'success') {
                addLog('✅ Success', 'success');
                if (cmdResult.output) {
                  // Parse and display JSON output nicely if it's JSON
                  try {
                    const jsonOutput = JSON.parse(cmdResult.output);
                    if (jsonOutput.update && jsonOutput.update.id) {
                      addLog(`📝 Update ID: ${jsonOutput.update.id}`, 'success');
                      addLog(`📊 Status: ${jsonOutput.update.status}`, 'info');
                      if (jsonOutput.update.type) {
                        addLog(`🔧 Type: ${jsonOutput.update.type}`, 'info');
                      }
                    } else {
                      // Display raw JSON
                      addLog(cmdResult.output, 'info');
                    }
                  } catch {
                    // Not JSON, display as is
                    addLog(cmdResult.output, 'info');
                  }
                }
              } else {
                addLog('❌ Failed', 'error');

                // Display categorized error information from backend
                if (cmdResult.errorCategory) {
                  addLog('', 'error');
                  addLog(`━━━ ${cmdResult.errorCategory} ━━━`, 'error');
                }

                if (cmdResult.actionableMessage) {
                  addLog(`💡 ${cmdResult.actionableMessage}`, 'warning');
                } else if (cmdResult.error) {
                  addLog(`Error: ${cmdResult.error}`, 'error');
                }

                // Show required permissions if available
                if (cmdResult.requiredPermissions && cmdResult.requiredPermissions.length > 0) {
                  addLog('', 'warning');
                  addLog('📋 Required IAM Permissions:', 'warning');
                  cmdResult.requiredPermissions.forEach((perm: string) => {
                    addLog(`   • ${perm}`, 'warning');
                  });
                  addLog('', 'info');
                  addLog('👉 Add these permissions to your IAM role and retry', 'info');
                }

                // Show if retryable
                if (cmdResult.retryable) {
                  addLog('', 'info');
                  addLog('🔄 This operation can be retried after fixing the issue', 'info');
                }

                if (cmdResult.stderr) {
                  addLog('', 'error');
                  addLog(`Technical Details: ${cmdResult.stderr}`, 'error');
                }
              }
              addLog('', 'info');
            });
          }

          // Check if this was a cluster upgrade command
          const wasUpgradeCommand = solution.awsCommands?.some((cmd: string) =>
            cmd.includes('update-cluster-version')
          );

          if (wasUpgradeCommand) {
            addLog('🔄 Cluster upgrade initiated!', 'success');
            addLog('', 'info');
            addLog('📡 Starting real-time status monitoring...', 'info');
            addLog('Polling AWS every 10 seconds for updates', 'info');
            addLog('', 'info');

            // Save upgrade state
            const targetVersion = solution.awsCommands[0]?.match(/--version\s+(\S+)/)?.[1];
            if (targetVersion) {
              navigationState.goToUpgradeProgress(
                clusterId,
                clusterName,
                targetVersion,
                15 // 15 minutes estimated
              );
            }

            // DON'T hide wizard - keep buttons visible but disabled
            // Start monitoring - this will update progress bar
            startProgressMonitoring();
          }

          // Check if there were any failures in the results
          const hadFailures = result.results && result.results.some((r: any) => r.status === 'failed');
          const hadRetryableFailures = result.results && result.results.some((r: any) => r.status === 'failed' && r.retryable);

          if (hadRetryableFailures) {
            // Keep wizard visible and keep error analysis for retry
            setShowWizard(true);
          } else if (!hadFailures) {
            // Only remove on complete success
            sessionStorage.removeItem('llmErrorAnalysis');
          }
        } else {
          const errorData = await response.json();
          addLog('❌ Command execution failed', 'error');
          addLog(`Error: ${errorData.message || 'Unknown error'}`, 'error');

          // Display individual command failures with categorized errors
          if (errorData.results) {
            let hasRetryableErrors = false;

            errorData.results.forEach((cmdResult: any, idx: number) => {
              if (cmdResult.status === 'failed') {
                addLog('', 'error');
                addLog(`━━━ Command ${idx + 1} Failed ━━━`, 'error');
                addLog(`$ ${cmdResult.command}`, 'info');
                addLog('', 'error');

                // Display categorized error information
                if (cmdResult.errorCategory) {
                  addLog(`📂 Category: ${cmdResult.errorCategory}`, 'error');
                }

                if (cmdResult.actionableMessage) {
                  addLog(`💡 ${cmdResult.actionableMessage}`, 'warning');
                } else if (cmdResult.error) {
                  addLog(`Error: ${cmdResult.error}`, 'error');
                }

                // Show required permissions
                if (cmdResult.requiredPermissions && cmdResult.requiredPermissions.length > 0) {
                  addLog('', 'warning');
                  addLog('📋 Required IAM Permissions:', 'warning');
                  cmdResult.requiredPermissions.forEach((perm: string) => {
                    addLog(`   • ${perm}`, 'warning');
                  });
                  addLog('', 'info');
                  addLog('👉 Add these permissions to your IAM role', 'info');
                }

                // Track retryable errors
                if (cmdResult.retryable) {
                  hasRetryableErrors = true;
                  addLog('', 'info');
                  addLog('🔄 This command can be retried after fixing the issue', 'info');
                }

                if (cmdResult.stderr) {
                  addLog('', 'error');
                  addLog(`Technical Details: ${cmdResult.stderr}`, 'error');
                }
              }
            });

            // Show retry option if there are retryable errors
            if (hasRetryableErrors) {
              addLog('', 'success');
              addLog('═══════════════════════════════════════════', 'success');
              addLog('🔄 RETRY AVAILABLE', 'success');
              addLog('═══════════════════════════════════════════', 'success');
              addLog('', 'info');
              addLog('After fixing the permission or access issues:', 'info');
              addLog('1. Add required IAM permissions to your role', 'info');
              addLog('2. Click the same solution button again to retry', 'info');
              addLog('', 'info');

              // Keep wizard visible so user can retry
              setShowWizard(true);
              // DON'T remove llmErrorAnalysis - keep it so buttons remain visible
            } else {
              // Only remove error analysis if all commands succeeded or errors are not retryable
              sessionStorage.removeItem('llmErrorAnalysis');
            }
          } else {
            // No results - remove error analysis
            sessionStorage.removeItem('llmErrorAnalysis');
          }
        }
      } catch (err: any) {
        addLog('❌ Network error during command execution', 'error');
        addLog(`${err.message}`, 'error');
      }
    }

    setIsExecutingCommand(false);
    setShowConfirmation(false);
    setPendingSolution(null);
  };

  const breadcrumbs = navigationState.getBreadcrumbs();

  return (
    <div className="p-2 space-y-2 max-w-6xl mx-auto">
      {/* Breadcrumb Navigation */}
      <Breadcrumb items={breadcrumbs} />

      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <button
          onClick={onBack}
          className="flex items-center space-x-1 text-xs text-gray-600 hover:text-gray-900 transition-colors"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="text-xs" />
          <span>Back</span>
        </button>
      </div>

      {/* Title */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-md p-2.5">
        <h1 className="text-base font-bold text-gray-900 flex items-center">
          <FontAwesomeIcon icon={faRocket} className="mr-1.5 text-sm text-blue-600" />
          EKS Upgrade
        </h1>
        <p className="text-xs text-gray-600 mt-0.5">{clusterName}</p>
      </div>

      {/* Content */}
      <div className="space-y-2">
        {loading ? (
          <div className="text-center py-6">
            <FontAwesomeIcon icon={faSpinner} className="text-lg text-blue-600 animate-spin mb-2" />
            <p className="text-xs text-gray-600">Analyzing...</p>
          </div>
        ) : upgradeAnalysis ? (
          <>
            {/* Show wizard elements when not started OR when there are errors to fix */}
            {showWizard && (
              <>
              {/* Current Status */}
              <div className="bg-white border border-gray-200 rounded p-2">
                <h2 className="text-sm font-bold text-gray-900 mb-2">
                  {isNodeSync ? 'Node Group Synchronization' : 'Version'}
                </h2>
                {isNodeSync ? (
                  <div className="space-y-2">
                    <div className="bg-blue-50 border border-blue-200 rounded p-2">
                      <p className="text-xs text-blue-900 mb-1">
                        <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
                        Syncing node groups to control plane version
                      </p>
                      <div className="text-sm font-bold text-blue-700">
                        Target Version: {upgradeAnalysis.cluster.currentVersion}
                      </div>
                    </div>
                    {upgradeAnalysis.nodeGroups && upgradeAnalysis.nodeGroups.length > 0 && (
                      <div className="space-y-1">
                        <div className="text-xs text-gray-500">Node Groups to Upgrade:</div>
                        {upgradeAnalysis.nodeGroups.map((ng: any, idx: number) => (
                          <div key={idx} className="flex justify-between text-xs bg-gray-50 p-1.5 rounded">
                            <span className="font-medium">{ng.name}</span>
                            <span className="text-gray-600">
                              {ng.version} → {upgradeAnalysis.cluster.currentVersion}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="text-xs text-gray-500">Current</div>
                      <div className="text-base font-bold text-gray-900">
                        {upgradeAnalysis.cluster.currentVersion}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Target</div>
                      <div className="text-base font-bold text-green-600">
                        {upgradeAnalysis.upgradeOptions.nextVersion || 'Up to date'}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Upgrade Path Visualization */}
              {upgradeAnalysis.upgradeOptions.upgradePath && upgradeAnalysis.upgradeOptions.upgradePath.length > 1 && (
                <div className="bg-blue-50 border border-blue-200 rounded p-2">
                  <h3 className="text-xs font-semibold text-blue-900 mb-1.5 flex items-center">
                    <FontAwesomeIcon icon={faInfoCircle} className="mr-1 text-xs" />
                    Multi-Step Upgrade Required
                  </h3>
                  <p className="text-xs text-blue-800 mb-2">
                    EKS requires upgrading one version at a time. This upgrade will follow the path:
                  </p>
                  <div className="flex items-center space-x-1 flex-wrap">
                    <span className="px-2 py-1 bg-gray-200 text-gray-900 rounded text-xs font-semibold">
                      {upgradeAnalysis.cluster.currentVersion}
                    </span>
                    {upgradeAnalysis.upgradeOptions.upgradePath.map((version: string, index: number) => (
                      <React.Fragment key={index}>
                        <FontAwesomeIcon icon={faArrowLeft} className="rotate-180 text-blue-600 text-xs" />
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          index === 0 ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {version}
                        </span>
                      </React.Fragment>
                    ))}
                  </div>
                  <p className="text-xs text-blue-700 mt-2">
                    First upgrade: <strong>{upgradeAnalysis.cluster.currentVersion} → {upgradeAnalysis.upgradeOptions.nextVersion}</strong>
                    {upgradeAnalysis.upgradeOptions.upgradePath.length > 1 && (
                      <span className="block mt-0.5">
                        Subsequent upgrades will be available after this completes.
                      </span>
                    )}
                  </p>
                </div>
              )}

              {/* Warnings */}
              {upgradeAnalysis.warnings && upgradeAnalysis.warnings.length > 0 && (
                <div className="bg-yellow-50 border-l-2 border-yellow-500 p-2 rounded-sm">
                  <h3 className="text-xs font-semibold text-yellow-900 mb-1 flex items-center">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1 text-xs" />
                    Warnings
                  </h3>
                  <ul className="space-y-0.5">
                    {upgradeAnalysis.warnings.map((warning: any, index: number) => (
                      <li key={index} className="text-xs text-yellow-800">
                        {warning.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Upgrade Mode Selection */}
              <div className="bg-white border border-gray-200 rounded p-2 relative z-10">
                <h2 className="text-sm font-bold text-gray-900 mb-2">Upgrade Mode</h2>
                <div className="space-y-2 relative z-20">
                  {/* Zero-Downtime Option */}
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      console.log('Zero-downtime clicked', e);
                      setUpgradeMode('zero-downtime');
                    }}
                    onMouseEnter={() => console.log('Mouse entered zero-downtime')}
                    style={{ position: 'relative', zIndex: 100 }}
                    className={`w-full text-left p-2 rounded border-2 transition-all cursor-pointer select-none ${
                      upgradeMode === 'zero-downtime'
                        ? 'border-blue-500 bg-blue-50 shadow-md ring-2 ring-blue-200'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start">
                      <div className="mt-0.5 mr-2">
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                          upgradeMode === 'zero-downtime' ? 'border-blue-600' : 'border-gray-300'
                        }`}>
                          {upgradeMode === 'zero-downtime' && (
                            <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                          )}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className={`text-xs font-semibold mb-0.5 ${
                          upgradeMode === 'zero-downtime' ? 'text-blue-700' : 'text-gray-900'
                        }`}>
                          Zero-Downtime Rolling Update
                        </div>
                        <div className="text-xs text-gray-600 mb-1">
                          Gradual node replacement with no service interruption
                        </div>
                        {upgradeAnalysis.estimatedTime?.zeroDowntime && (
                          <div className="flex items-center text-xs text-blue-600">
                            <FontAwesomeIcon icon={faClock} className="mr-1" />
                            ~{upgradeAnalysis.estimatedTime.zeroDowntime.total} minutes
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Force Upgrade Option */}
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      console.log('Force mode clicked', e);
                      setUpgradeMode('force');
                    }}
                    onMouseEnter={() => console.log('Mouse entered force mode')}
                    style={{ position: 'relative', zIndex: 100 }}
                    className={`w-full text-left p-2 rounded border-2 transition-all cursor-pointer select-none ${
                      upgradeMode === 'force'
                        ? 'border-orange-500 bg-orange-50 shadow-md ring-2 ring-orange-200'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start">
                      <div className="mt-0.5 mr-2">
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                          upgradeMode === 'force' ? 'border-orange-600' : 'border-gray-300'
                        }`}>
                          {upgradeMode === 'force' && (
                            <div className="w-2 h-2 rounded-full bg-orange-600"></div>
                          )}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className={`text-xs font-semibold mb-0.5 ${
                          upgradeMode === 'force' ? 'text-orange-700' : 'text-gray-900'
                        }`}>
                          Force Upgrade (Faster)
                        </div>
                        <div className="text-xs text-orange-600 mb-1">
                          ⚠️ May cause temporary service disruption
                        </div>
                        {upgradeAnalysis.estimatedTime?.forceUpgrade && (
                          <div className="flex items-center text-xs text-orange-600">
                            <FontAwesomeIcon icon={faClock} className="mr-1" />
                            ~{upgradeAnalysis.estimatedTime.forceUpgrade.total} minutes
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Estimated Time Breakdown */}
              {upgradeAnalysis.estimatedTime && (
                <div className="bg-white border border-gray-200 rounded p-2">
                  <h2 className="text-sm font-bold text-gray-900 mb-2">Estimated Breakdown</h2>
                  <div className="space-y-1 text-xs">
                    {upgradeMode === 'zero-downtime' ? (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Control Plane:</span>
                          <span className="font-medium">{upgradeAnalysis.estimatedTime.zeroDowntime.breakdown.controlPlane}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Node Groups:</span>
                          <span className="font-medium">{upgradeAnalysis.estimatedTime.zeroDowntime.breakdown.nodeGroups}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Addons:</span>
                          <span className="font-medium">{upgradeAnalysis.estimatedTime.zeroDowntime.breakdown.addons}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Control Plane:</span>
                          <span className="font-medium">{upgradeAnalysis.estimatedTime.forceUpgrade.breakdown.controlPlane}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Node Groups:</span>
                          <span className="font-medium">{upgradeAnalysis.estimatedTime.forceUpgrade.breakdown.nodeGroups}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* LLM-Generated Intelligent Fix Options */}
              {(() => {
                const llmAnalysisStr = sessionStorage.getItem('llmErrorAnalysis');
                if (!llmAnalysisStr) return null;
                const llmData = JSON.parse(llmAnalysisStr);
                if (!llmData.showFixOptions || !llmData.analysis.solutions) return null;

                const analysis = llmData.analysis;

                return (
                  <div className="bg-purple-50 border-2 border-purple-300 rounded p-3 relative z-50">
                    <h3 className="text-sm font-bold text-purple-900 mb-2 flex items-center">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1.5" />
                      🤖 AI-Detected Issue
                    </h3>
                    <div className="bg-white border border-purple-200 rounded p-2 mb-3">
                      <div className="text-xs space-y-1">
                        <div><strong>Root Cause:</strong> {analysis.rootCause}</div>
                        <div><strong>Criticality:</strong> <span className={`font-bold ${
                          analysis.criticality === 'critical' ? 'text-red-600' :
                          analysis.criticality === 'high' ? 'text-orange-600' :
                          analysis.criticality === 'medium' ? 'text-yellow-600' : 'text-green-600'
                        }`}>{analysis.criticality.toUpperCase()}</span></div>
                        <div><strong>Impact:</strong> {analysis.impact}</div>
                      </div>
                    </div>

                    <p className="text-xs text-purple-800 mb-3 font-semibold">
                      AI has analyzed your cluster and recommends the following solutions:
                    </p>

                    <div className="space-y-2 relative z-50">
                      {analysis.solutions.map((solution: any, idx: number) => {
                        const isAutomated = solution.automated;
                        const bgColor = idx === 0 ? 'bg-green-600 hover:bg-green-700' :
                                       idx === 1 ? 'bg-blue-600 hover:bg-blue-700' :
                                       'bg-gray-600 hover:bg-gray-700';

                        return (
                          <button
                            key={idx}
                            type="button"
                            style={{ position: 'relative', zIndex: 999, pointerEvents: 'auto' }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              console.log(`AI Solution ${idx + 1} clicked! Opening confirmation modal...`, solution);

                              // Show confirmation modal instead of executing directly
                              setPendingSolution(solution);
                              setShowConfirmation(true);
                            }}
                            className={`w-full px-3 py-2 text-xs ${bgColor} text-white rounded text-left transition-colors`}
                          >
                            <div className="font-bold">
                              {idx === 0 ? '⭐' : idx === 1 ? '✨' : '🔧'} Option {idx + 1}: {solution.title}
                            </div>
                            <div className="text-xs opacity-90 mt-0.5">{solution.description}</div>
                            <div className="text-xs opacity-75 mt-1 flex items-center space-x-3">
                              <span>⏱️ {solution.estimatedDowntime}</span>
                              <span>{isAutomated ? '🤖 Automated' : '👨‍💻 Manual'}</span>
                              <span className={`font-semibold ${
                                solution.criticality === 'high' ? 'text-red-200' :
                                solution.criticality === 'medium' ? 'text-yellow-200' : 'text-green-200'
                              }`}>
                                {solution.criticality.toUpperCase()}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    {analysis.preventionTips && analysis.preventionTips.length > 0 && (
                      <div className="mt-3 bg-blue-50 border border-blue-200 rounded p-2">
                        <div className="text-xs font-semibold text-blue-900 mb-1">🛡️ Prevention Tips:</div>
                        <ul className="text-xs text-blue-800 space-y-0.5 list-disc list-inside">
                          {analysis.preventionTips.map((tip: string, idx: number) => (
                            <li key={idx}>{tip}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* AMI Migration Fix Options (Backward Compatible) */}
              {(() => {
                const errorStr = sessionStorage.getItem('amiMigrationError');
                if (!errorStr) return null;
                const error = JSON.parse(errorStr);
                if (!error.showFixOptions) return null;

                return (
                  <div className="bg-red-50 border-2 border-red-300 rounded p-3" style={{ position: 'relative', zIndex: 1000 }}>
                    <h3 className="text-sm font-bold text-red-900 mb-2 flex items-center">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1.5" />
                      AMI Migration Required
                    </h3>
                    <p className="text-xs text-red-800 mb-3">
                      Your node group <strong>"{error.nodeGroupName}"</strong> uses AL2 AMI, which doesn't support Kubernetes {error.targetVersion}.
                      AWS requires AL2023 AMI for Kubernetes 1.33+.
                    </p>

                    <div className="space-y-2" style={{ position: 'relative', zIndex: 1001 }}>
                      <button
                        type="button"
                        disabled={executingFix !== null}
                        style={{ position: 'relative', zIndex: 1002, pointerEvents: executingFix !== null ? 'none' : 'auto' }}
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('✨ Option 1 clicked! Creating AL2023 node group automatically');

                          // Set loading state for Option 1 only
                          setExecutingFix('option1');
                          setUpgradeStarted(true); // Show logs section
                          setActiveTab('logs'); // Switch to logs tab

                          // Option 1: Automatic creation
                          addLog('', 'info');
                          addLog('════════════════════════════════════════════', 'info');
                          addLog('✨ OPTION 1 SELECTED: AUTOMATIC FIX', 'success');
                          addLog('════════════════════════════════════════════', 'info');
                          addLog('', 'info');
                          addLog('🔧 Creating new AL2023 node group...', 'info');
                          addLog('This will create a new node group with AL2023 AMI type', 'info');
                          addLog('', 'info');

                          try {
                            const response = await fetch(
                              `${API_BASE_URL}/api/accounts/eks/clusters/${clusterId}/create-nodegroup-al2023`,
                              {
                                method: 'POST',
                                headers: {
                                  'Authorization': `Bearer ${authToken}`,
                                  'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({
                                  clusterName: error.clusterName,
                                  nodeGroupName: `${error.nodeGroupName}-al2023`,
                                  targetVersion: error.targetVersion,
                                  sourceNodeGroup: error.nodeGroupName
                                })
                              }
                            );

                            if (response.ok) {
                              const result = await response.json();
                              addLog('', 'success');
                              addLog('════════════════════════════════════════════', 'success');
                              addLog('✅ NODE GROUP CREATION INITIATED', 'success');
                              addLog('════════════════════════════════════════════', 'success');
                              addLog('', 'success');
                              addLog(`📦 New node group: ${result.nodeGroupName}`, 'success');
                              addLog(`🔧 AMI Type: ${result.amiType}`, 'success');
                              addLog(`📍 Version: ${result.version}`, 'success');
                              addLog(`⏳ Estimated time: ${result.estimatedTime}`, 'info');
                              addLog('', 'info');
                              addLog('📋 NEXT STEPS:', 'warning');
                              result.nextSteps.forEach((step: string, idx: number) => {
                                addLog(`${idx + 1}. ${step}`, 'warning');
                              });
                              addLog('', 'info');
                              sessionStorage.removeItem('amiMigrationError');
                            } else {
                              const errorData = await response.json();
                              addLog('', 'error');
                              addLog('════════════════════════════════════════════', 'error');
                              addLog('❌ FAILED TO CREATE NODE GROUP', 'error');
                              addLog('════════════════════════════════════════════', 'error');
                              addLog('', 'error');
                              addLog(`Error: ${errorData.message || 'Unknown error'}`, 'error');
                              if (errorData.error) {
                                addLog(`Details: ${errorData.error}`, 'error');
                              }
                              if (errorData.errorCode) {
                                addLog(`Error Code: ${errorData.errorCode}`, 'error');
                              }
                              if (errorData.errorDetails) {
                                if (errorData.errorDetails.statusCode) {
                                  addLog(`Status Code: ${errorData.errorDetails.statusCode}`, 'error');
                                }
                                if (errorData.errorDetails.requestId) {
                                  addLog(`Request ID: ${errorData.errorDetails.requestId}`, 'error');
                                }
                              }
                              addLog('', 'error');

                              // Trigger LLM analysis for this error
                              addLog('🤖 Analyzing error with AI...', 'info');
                              addLog('', 'info');

                              try {
                                const analysisResponse = await fetch(
                                  `${API_BASE_URL}/api/accounts/eks/clusters/${clusterId}/analyze-error`,
                                  {
                                    method: 'POST',
                                    headers: {
                                      'Authorization': `Bearer ${authToken}`,
                                      'Content-Type': 'application/json'
                                    },
                                    body: JSON.stringify({
                                      operation: 'create_al2023_nodegroup',
                                      errorMessage: errorData.error || errorData.message,
                                      errorContext: {
                                        errorCode: errorData.errorCode,
                                        statusCode: errorData.errorDetails?.statusCode,
                                        nodeGroupName: error.nodeGroupName,
                                        targetVersion: error.targetVersion
                                      }
                                    })
                                  }
                                );

                                if (analysisResponse.ok) {
                                  const llmResult = await analysisResponse.json();
                                  console.log('LLM Analysis Result:', llmResult);
                                  if (llmResult.analysis && llmResult.analysis.success) {
                                    const analysis = llmResult.analysis;

                                    addLog('🤖 AI ANALYSIS COMPLETE', 'success');
                                    addLog('', 'info');
                                    addLog(`🔍 ROOT CAUSE: ${analysis.rootCause}`, 'warning');
                                    addLog(`⚠️ CRITICALITY: ${analysis.criticality.toUpperCase()}`, 'warning');
                                    addLog(`📊 IMPACT: ${analysis.impact}`, 'warning');
                                    addLog('', 'info');

                                    if (analysis.solutions && analysis.solutions.length > 0) {
                                      addLog('💡 RECOMMENDED SOLUTIONS:', 'success');
                                      addLog('', 'success');

                                      analysis.solutions.forEach((sol: any, idx: number) => {
                                        addLog(`═══ Option ${idx + 1}: ${sol.title} ═══`, 'success');
                                        addLog(`📝 Description: ${sol.description}`, 'info');
                                        addLog(`⏱️ Downtime: ${sol.estimatedDowntime}`, 'info');
                                        addLog(`🤖 Automated: ${sol.automated ? 'Yes' : 'No'}`, 'info');
                                        addLog('', 'info');

                                        if (sol.steps && sol.steps.length > 0) {
                                          addLog('📋 STEPS:', 'info');
                                          sol.steps.forEach((step: string, stepIdx: number) => {
                                            addLog(`   ${stepIdx + 1}. ${step}`, 'info');
                                          });
                                          addLog('', 'info');
                                        }

                                        if (sol.awsCommands && sol.awsCommands.length > 0) {
                                          addLog('💻 AWS CLI COMMANDS:', 'warning');
                                          sol.awsCommands.forEach((cmd: string) => {
                                            addLog(`   ${cmd}`, 'info');
                                          });
                                          addLog('', 'info');
                                        }
                                      });
                                    }

                                    if (analysis.preventionTips && analysis.preventionTips.length > 0) {
                                      addLog('🛡️ PREVENTION TIPS:', 'info');
                                      analysis.preventionTips.forEach((tip: string) => {
                                        addLog(`   • ${tip}`, 'info');
                                      });
                                      addLog('', 'info');
                                    }
                                  }
                                } else {
                                  const errorText = await analysisResponse.text();
                                  console.error('AI analysis request failed:', analysisResponse.status, errorText);
                                  addLog(`⚠️ AI analysis request failed (${analysisResponse.status})`, 'warning');
                                  addLog(`Check backend logs or AWS credentials`, 'warning');
                                }
                              } catch (analysisError: any) {
                                console.error('LLM analysis failed:', analysisError);
                                addLog('⚠️ Could not analyze error with AI', 'warning');
                                addLog(`Error: ${analysisError.message}`, 'warning');
                              }

                              addLog('════════════════════════════════════════════', 'error');
                            }
                          } catch (err: any) {
                            addLog('', 'error');
                            addLog('════════════════════════════════════════════', 'error');
                            addLog('❌ NETWORK ERROR', 'error');
                            addLog('════════════════════════════════════════════', 'error');
                            addLog('', 'error');
                            addLog(`Error: ${err.message}`, 'error');
                            addLog('', 'error');
                            addLog('════════════════════════════════════════════', 'error');
                          } finally {
                            // Keep the button highlighted - don't reset
                            // User can see which option was selected
                          }
                        }}
                        className={`w-full px-3 py-2 text-xs rounded text-left transition-colors ${
                          executingFix === 'option1'
                            ? 'bg-green-700 ring-2 ring-green-400 text-white'
                            : executingFix === 'option2'
                            ? 'bg-gray-400 text-gray-600 cursor-not-allowed opacity-60'
                            : 'bg-green-600 hover:bg-green-700 text-white cursor-pointer'
                        }`}
                      >
                        <div className="font-bold flex items-center">
                          {executingFix === 'option1' && <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />}
                          ✨ Option 1: Automatic Fix
                        </div>
                        <div className="text-xs opacity-90 mt-0.5">
                          {executingFix === 'option1' ? 'Creating new AL2023 node group...' : 'Create new AL2023 node group automatically'}
                        </div>
                      </button>

                      <button
                        type="button"
                        disabled={executingFix !== null}
                        style={{ position: 'relative', zIndex: 1002, pointerEvents: executingFix !== null ? 'none' : 'auto' }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('📖 Option 2 clicked! Showing manual migration instructions');

                          // Set loading state for Option 2 only
                          setExecutingFix('option2');
                          setUpgradeStarted(true); // Show logs section
                          setActiveTab('logs'); // Switch to logs tab

                          // Option 2: Show manual instructions
                          addLog('', 'info');
                          addLog('════════════════════════════════════════════', 'info');
                          addLog('📖 OPTION 2 SELECTED: MANUAL MIGRATION', 'info');
                          addLog('════════════════════════════════════════════', 'info');
                          addLog('', 'info');
                          addLog('📋 MANUAL MIGRATION INSTRUCTIONS:', 'info');
                          addLog('', 'info');
                          addLog('Step 1: Create new AL2023 node group', 'info');
                          addLog('', 'info');
                          addLog('💻 AWS CLI COMMAND:', 'warning');
                          addLog(`aws eks create-nodegroup \\`, 'info');
                          addLog(`  --cluster-name ${error.clusterName} \\`, 'info');
                          addLog(`  --nodegroup-name ${error.nodeGroupName}-al2023 \\`, 'info');
                          addLog(`  --ami-type AL2023_x86_64_STANDARD \\`, 'info');
                          addLog(`  --node-role <your-node-role-arn> \\`, 'info');
                          addLog(`  --subnets <subnet-id-1> <subnet-id-2> \\`, 'info');
                          addLog(`  --version ${error.targetVersion}`, 'info');
                          addLog('', 'info');
                          addLog('Step 2: Wait for node group to become ACTIVE', 'info');
                          addLog('   Monitor: aws eks describe-nodegroup --cluster-name ${error.clusterName} --nodegroup-name ${error.nodeGroupName}-al2023', 'info');
                          addLog('', 'info');
                          addLog('Step 3: Drain and cordon old nodes', 'info');
                          addLog('   kubectl drain <node-name> --ignore-daemonsets --delete-emptydir-data', 'info');
                          addLog('', 'info');
                          addLog('Step 4: Verify workloads moved to new nodes', 'info');
                          addLog('   kubectl get pods -o wide', 'info');
                          addLog('', 'info');
                          addLog('Step 5: Delete old AL2 node group', 'info');
                          addLog(`   aws eks delete-nodegroup --cluster-name ${error.clusterName} --nodegroup-name ${error.nodeGroupName}`, 'info');
                          addLog('', 'info');
                          addLog('📚 Full documentation: https://docs.aws.amazon.com/eks/latest/userguide/update-node-group.html', 'info');
                          addLog('', 'info');
                          addLog('════════════════════════════════════════════', 'info');

                          sessionStorage.removeItem('amiMigrationError');

                          // Keep the button highlighted - don't reset
                          // User can see which option was selected
                        }}
                        className={`w-full px-3 py-2 text-xs rounded text-left transition-colors ${
                          executingFix === 'option2'
                            ? 'bg-blue-700 ring-2 ring-blue-400 text-white'
                            : executingFix === 'option1'
                            ? 'bg-gray-400 text-gray-600 cursor-not-allowed opacity-60'
                            : 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer'
                        }`}
                      >
                        <div className="font-bold flex items-center">
                          {executingFix === 'option2' && <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />}
                          📖 Option 2: Manual Migration
                        </div>
                        <div className="text-xs opacity-90 mt-0.5">
                          {executingFix === 'option2' ? 'Loading manual instructions...' : 'Show AWS CLI commands for manual setup'}
                        </div>
                      </button>
                    </div>
                  </div>
                );
              })()}

              {/* Progress Bar - Show when upgrade is in progress */}
              {upgradeProgress.progress > 0 && upgradeProgress.progress < 100 && (
                <div className="bg-white border-2 border-blue-300 rounded p-3">
                  <h3 className="text-sm font-bold text-gray-900 mb-2">Upgrade Progress</h3>

                  {/* Progress Bar */}
                  <div className="mb-3">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs text-gray-600">{upgradeProgress.phase}</span>
                      <span className="text-sm font-bold text-blue-600">{upgradeProgress.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className="bg-gradient-to-r from-green-600 to-blue-600 h-3 rounded-full transition-all duration-500"
                        style={{ width: `${upgradeProgress.progress}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Time Info */}
                  <div className="flex justify-between text-xs text-gray-600">
                    <div>
                      <FontAwesomeIcon icon={faClock} className="mr-1" />
                      Elapsed: {Math.floor(upgradeProgress.elapsed / 60)}m {upgradeProgress.elapsed % 60}s
                    </div>
                    <div>
                      ETA: ~{15 - Math.floor(upgradeProgress.elapsed / 60)} minutes remaining
                    </div>
                  </div>

                  {/* Status Message */}
                  <div className="mt-2 text-xs text-gray-700">
                    {upgradeProgress.phase === 'control_plane_upgrading' && '🔄 Upgrading control plane... This may take 10-15 minutes'}
                    {upgradeProgress.phase === 'pending_node_group_upgrade' && '⏳ Control plane upgraded! Node groups pending...'}
                    {upgradeProgress.phase === 'node_groups_upgrading' && '🔄 Upgrading node groups... Nearly complete'}
                  </div>
                </div>
              )}

              {/* Completion Banner - Show when upgrade completes */}
              {upgradeProgress.progress === 100 && (
                <div className="bg-green-50 border-2 border-green-200 rounded p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 text-lg" />
                      <span className="text-sm font-bold text-green-900">
                        {isNodeSync ? 'Node synchronization complete!' : 'Upgrade completed successfully!'}
                      </span>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-green-800">
                    Cluster is now running the target version. Check the Logs tab for full details.
                  </div>
                  <div className="mt-3 flex space-x-2">
                    <button
                      onClick={onBack}
                      className="px-3 py-1.5 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors"
                    >
                      <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                      Return to Cluster Details
                    </button>
                    <button
                      onClick={() => {
                        navigationState.clearUpgradeState();
                        setShowWizard(true);
                        setUpgradeStarted(false);
                        setUpgradeProgress({ phase: 'idle', progress: 0, elapsed: 0, remaining: 0, logs: [] });
                      }}
                      className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200 transition-colors"
                    >
                      Start Another Upgrade
                    </button>
                  </div>
                </div>
              )}

              {/* Start Upgrade Button */}
              <div className="flex justify-end space-x-2 relative z-30">
                <button
                  type="button"
                  onClick={onBack}
                  disabled={upgrading || upgradeProgress.progress > 0}
                  className={`px-3 py-1 text-xs border border-gray-300 rounded transition-colors ${
                    (upgrading || upgradeProgress.progress > 0)
                      ? 'opacity-50 cursor-not-allowed text-gray-400 bg-gray-100'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    console.log('Start Upgrade clicked!', {
                      isNodeSync,
                      nextVersion: upgradeAnalysis.upgradeOptions.nextVersion,
                      currentVersion: upgradeAnalysis.cluster.currentVersion,
                      upgradeMode,
                      disabled: (!isNodeSync && !upgradeAnalysis.upgradeOptions.nextVersion) || upgrading
                    });
                    // For node sync, use current control plane version as target
                    const targetVersion = isNodeSync
                      ? upgradeAnalysis.cluster.currentVersion
                      : upgradeAnalysis.upgradeOptions.nextVersion;
                    handleStartUpgrade(targetVersion, upgradeMode);
                  }}
                  disabled={(!isNodeSync && !upgradeAnalysis.upgradeOptions.nextVersion) || upgrading || upgradeProgress.progress > 0}
                  style={{ position: 'relative', zIndex: 200 }}
                  className={`px-3 py-1 text-xs text-white rounded transition-all ${
                    ((!isNodeSync && !upgradeAnalysis.upgradeOptions.nextVersion) || upgrading || upgradeProgress.progress > 0)
                      ? 'opacity-50 cursor-not-allowed bg-gray-400'
                      : (upgradeMode === 'force'
                        ? 'bg-orange-600 hover:bg-orange-700 cursor-pointer'
                        : 'bg-blue-600 hover:bg-blue-700 cursor-pointer')
                  }`}
                >
                  <FontAwesomeIcon icon={upgrading ? faSpinner : faRocket} className={`mr-1 text-xs ${upgrading ? 'animate-spin' : ''}`} />
                  {upgrading ? 'Starting...' : (isNodeSync ? `Start Node ${upgradeMode === 'force' ? 'Force ' : ''}Sync` : `Start ${upgradeMode === 'force' ? 'Force ' : ''}Upgrade`)}
                </button>
              </div>

              {/* Debug Info */}
              {(!isNodeSync && !upgradeAnalysis.upgradeOptions.nextVersion) && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-2 text-xs text-yellow-800">
                  <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
                  Cluster is up to date. Current version: {upgradeAnalysis.cluster.currentVersion}
                </div>
              )}
              </>
            )}

            {/* Tabbed Interface - Shows when upgrade started OR when there are logs */}
            {(upgradeStarted || upgradeProgress.logs.length > 0) && (
              <div className="bg-white border border-gray-200 rounded">
                {/* Tab Headers */}
                <div className="flex border-b border-gray-200">
                  <button
                    onClick={() => setActiveTab('overview')}
                    className={`px-4 py-2 text-xs font-semibold transition-colors ${
                      activeTab === 'overview'
                        ? 'text-blue-600 border-b-2 border-blue-600'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Overview
                  </button>
                  <button
                    onClick={() => setActiveTab('logs')}
                    className={`px-4 py-2 text-xs font-semibold transition-colors relative ${
                      activeTab === 'logs'
                        ? 'text-blue-600 border-b-2 border-blue-600'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Logs
                    {upgradeProgress.logs.length > 0 && (
                      <span className="ml-1 px-1.5 py-0.5 bg-blue-500 text-white rounded-full text-xs">
                        {upgradeProgress.logs.length}
                      </span>
                    )}
                  </button>
                </div>

                {/* Tab Content */}
                <div className="p-3">
                  {/* Overview Tab */}
                  {activeTab === 'overview' && (
                    <div className="space-y-2">
                      {/* Progress Bar */}
                      {upgradeStarted && !showWizard && (
                  <>
                    <div className="flex justify-between items-center">
                      <button
                        onClick={() => {
                          navigationState.clearUpgradeState();
                          setShowWizard(true);
                          setUpgradeStarted(false);
                          setUpgradeProgress({ phase: 'idle', progress: 0, elapsed: 0, remaining: 0, logs: [] });
                        }}
                        className="px-3 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700"
                      >
                        ← Start New Upgrade
                      </button>
                    </div>

                    <div className="bg-white border border-gray-200 rounded p-2">
                      <div className="flex justify-between items-center mb-2">
                        <h2 className="text-sm font-bold text-gray-900">Progress</h2>
                        <span className="text-base font-bold text-blue-600">{upgradeProgress.progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-gradient-to-r from-green-600 to-blue-600 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${upgradeProgress.progress}%` }}
                        ></div>
                      </div>
                      <div className="flex justify-between mt-2 text-xs text-gray-500">
                        <span>{Math.floor(upgradeProgress.elapsed / 60)}m {upgradeProgress.elapsed % 60}s</span>
                        <span>{upgradeProgress.phase}</span>
                      </div>
                    </div>
                  </>
                )}

                      {/* Success/Completion */}
                      {upgradeProgress.progress === 100 && (
                        <div className="bg-green-50 border-l-2 border-green-500 p-2 rounded-sm">
                          <div className="flex items-center">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 text-base mr-2" />
                            <div>
                              <h3 className="font-bold text-green-900 text-sm">Complete!</h3>
                              <p className="text-green-700 text-xs">
                                {clusterName} upgraded successfully
                              </p>
                            </div>
                          </div>
                          <div className="mt-2">
                            <button
                              onClick={() => {
                                navigationState.clearUpgradeState();
                                onBack();
                              }}
                              className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                            >
                              Return to Cluster
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Logs Tab */}
                  {activeTab === 'logs' && (
                    <div className="space-y-2">
                      {/* Execution Tracker - Show when execution is being tracked */}
                      {showExecutionTracker && currentExecutionId && (
                        <ExecutionTracker
                          executionId={currentExecutionId}
                          clusterId={clusterId}
                          authToken={authToken}
                          onComplete={() => {
                            addLog('✅ Execution tracking complete!', 'success');
                            setShowExecutionTracker(false);
                          }}
                          onResume={() => {
                            addLog('🔄 Resuming execution...', 'info');
                          }}
                        />
                      )}

                      {upgradeProgress.logs.length > 0 ? (
                        <div className="bg-gray-900 rounded p-2 max-h-96 overflow-y-auto">
                          <div className="space-y-0.5 font-mono text-xs">
                            {upgradeProgress.logs.map((log, index) => (
                              <div
                                key={index}
                                className={`flex items-start space-x-1.5 ${
                                  log.type === 'error' ? 'text-red-400' :
                                  log.type === 'success' ? 'text-green-400' :
                                  log.type === 'warning' ? 'text-yellow-400' :
                                  'text-gray-300'
                                }`}
                              >
                                <span className="text-gray-500 flex-shrink-0 text-xs">{log.time}</span>
                                <span className="flex-1 text-xs">{log.message}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500 text-xs">
                          <FontAwesomeIcon icon={faInfoCircle} className="text-2xl mb-2" />
                          <p>No logs yet. Start an upgrade to see logs here.</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-6">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-lg mb-2 text-gray-400" />
            <p className="text-xs text-gray-500">Failed to load</p>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {pendingSolution && (
        <CommandConfirmationModal
          isOpen={showConfirmation}
          onClose={() => {
            setShowConfirmation(false);
            setPendingSolution(null);
          }}
          onConfirm={() => handleExecuteSolution(pendingSolution)}
          title={pendingSolution.title}
          description={pendingSolution.description}
          commands={pendingSolution.awsCommands || []}
          risks={pendingSolution.risks || []}
          benefits={pendingSolution.benefits || []}
          estimatedDowntime={pendingSolution.estimatedDowntime}
          isExecuting={isExecutingCommand}
          validation={pendingSolution.validation}
        />
      )}
    </div>
  );
};
